package dev.wyns.ventrova.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.Dp
import dev.wyns.ventrova.data.model.Place
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.ui.theme.LevelPalettes
import dev.wyns.ventrova.ui.theme.VentrovaColors

/**
 * Share of a menu's dishes (drinks excluded) that is vegan and vegetarian, as fractions of the full
 * ring; the rest of the ring stays empty.
 */
data class MenuShare(val vegan: Float, val vegetarian: Float)

/**
 * The ring of a place: fully green for a vegan place; green and brown filling the whole ring for a
 * vegetarian place (all brown without a menu); and for a place that also serves meat only the vegan
 * and vegetarian share of its menu. Null when that share is unknown (no analysed menu).
 */
val Place.menuShare: MenuShare?
    get() {
        val menu = menu?.takeIf { it.foodItems > 0 }
        return when (veganLevel) {
            VeganLevel.Vegan -> MenuShare(vegan = 1f, vegetarian = 0f)
            VeganLevel.Vegetarian -> {
                val vegan = menu?.let { it.veganItems.toFloat() / it.foodItems } ?: 0f
                MenuShare(vegan = vegan, vegetarian = 1f - vegan)
            }
            VeganLevel.VeganOptions -> menu?.let {
                MenuShare(
                    vegan = it.veganItems.toFloat() / it.foodItems,
                    vegetarian = it.vegetarianItems.toFloat() / it.foodItems,
                )
            }
        }
    }

/**
 * Ring of [width] along the edge of its bounds showing [share], running clockwise: vegan, then
 * vegetarian. An unknown share is drawn as the design's dashed grey ring
 * (`repeating-conic-gradient(#CFCBC1 0 12deg, #F4F2EC 12deg 24deg)`).
 *
 * With a [gap] (degrees) the ring becomes an arc that leaves that part open around [gapCenter]
 * (degrees, 0 = 3 o'clock, -90 = 12 o'clock), e.g. for the level glyph on a map marker; the share
 * is then spread over the remaining arc. Without a gap the ring starts at the top.
 */
@Composable
fun MenuRing(
    share: MenuShare?,
    width: Dp,
    modifier: Modifier = Modifier,
    alpha: Float = 1f,
    gap: Float = 0f,
    gapCenter: Float = -90f,
) {
    Canvas(modifier) {
        val stroke = width.toPx()
        val start = gapCenter + gap / 2
        val total = 360f - gap
        if (share == null) {
            ringArc(VentrovaColors.MarkerRingEmptyAlt, start, total, stroke, alpha)
            var angle = 0f
            while (angle < total) {
                ringArc(VentrovaColors.MarkerRingEmpty, start + angle, minOf(12f, total - angle), stroke, alpha)
                angle += 24f
            }
            return@Canvas
        }
        val vegan = share.vegan.coerceIn(0f, 1f) * total
        val vegetarian = share.vegetarian.coerceIn(0f, 1f - share.vegan.coerceIn(0f, 1f)) * total
        ringArc(VentrovaColors.MenuRingTrack, start, total, stroke, alpha)
        if (vegan > 0f) ringArc(LevelPalettes.Vegan.color, start, vegan, stroke, alpha)
        if (vegetarian > 0f) ringArc(LevelPalettes.Vegetarian.color, start + vegan, vegetarian, stroke, alpha)
    }
}

private fun DrawScope.ringArc(color: Color, start: Float, sweep: Float, stroke: Float, alpha: Float) {
    val inset = stroke / 2
    drawArc(
        color = color,
        startAngle = start,
        sweepAngle = sweep,
        useCenter = false,
        topLeft = Offset(inset, inset),
        size = Size(size.width - stroke, size.height - stroke),
        style = Stroke(width = stroke),
        alpha = alpha,
    )
}
